// Durable queue of scans waiting to reach the server.
//
// Scans are written here first and sent afterwards, so a scan is never lost to
// a slow or dead connection - and because it is IndexedDB rather than memory,
// the queue also survives a reload, a browser crash, or a closed laptop.
//
// Entries are only ever removed once the server has confirmed them, so the
// pending count shown to the operator is the truth, not an optimistic guess.

const DB_NAME = 'gbc-packing-list'
const DB_VERSION = 2
const STORE = 'scanQueue'

let dbPromise = null

function openDb() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      const store = db.objectStoreNames.contains(STORE)
        ? request.transaction.objectStore(STORE)
        : db.createObjectStore(STORE, { keyPath: 'opId' })

      // Counting pending work must not depend on how much of it there is.
      if (!store.indexNames.contains('eventId')) {
        store.createIndex('eventId', 'eventId')
      }
      // Lets a chunk be read oldest-first *within one event* straight from the
      // index, instead of loading every queued scan and filtering in JS.
      if (!store.indexNames.contains('eventQueuedAt')) {
        store.createIndex('eventQueuedAt', ['eventId', 'queuedAt'])
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

  return dbPromise
}

function runTx(mode, work) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode)
        const store = tx.objectStore(STORE)
        let result
        try {
          result = work(store)
        } catch (error) {
          reject(error)
          return
        }
        tx.oncomplete = () => resolve(result)
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(tx.error)
      })
  )
}

function requestValue(request) {
  // The value is read in oncomplete of the surrounding transaction, so the
  // request has always settled by the time the caller sees it.
  const box = { value: undefined }
  request.onsuccess = () => {
    box.value = request.result
  }
  return box
}

// By default IndexedDB is "best effort" storage: a browser low on disk may
// evict it without asking, which would silently destroy queued scans. Asking
// for persistent storage exempts us from that automatic eviction. It does not
// stop a human deliberately clearing site data - nothing does.
//
// Returns false if the browser declined, which is worth surfacing: it means
// the queue is evictable and should not be left unsynced for long.
export async function requestPersistentStorage() {
  try {
    if (!navigator.storage?.persist) return false
    if (await navigator.storage.persisted()) return true
    return await navigator.storage.persist()
  } catch (error) {
    console.error('Could not request persistent storage:', error)
    return false
  }
}

// crypto.randomUUID is only available in a secure context, and an internal
// tool may well be served over plain http on the LAN. Fall back to something
// collision-proof enough for one operator's scan stream.
export function newOpId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  const rand = () => Math.random().toString(36).slice(2, 10)
  return `${Date.now().toString(36)}-${rand()}-${rand()}`
}

export async function enqueueScan({ eventId, code, action, userId }) {
  const op = {
    opId: newOpId(),
    eventId,
    code,
    action,
    userId,
    scannedAt: new Date().toISOString(),
    queuedAt: Date.now(),
  }
  await runTx('readwrite', (store) => store.add(op))
  return op
}

function eventRange(eventId) {
  return IDBKeyRange.bound([eventId, 0], [eventId, Infinity])
}

// Oldest first, so scans reach the server in the order they were made. Walks
// the index with a cursor and stops at `limit` - reading the whole queue and
// slicing it would get slower exactly as the backlog grows.
export async function peekChunk(eventId, limit) {
  const collected = []
  await runTx('readonly', (store) => {
    const request = store.index('eventQueuedAt').openCursor(eventRange(eventId))
    request.onsuccess = () => {
      const cursor = request.result
      if (!cursor || collected.length >= limit) return
      collected.push(cursor.value)
      cursor.continue()
    }
  })
  return collected
}

export async function removeOps(opIds) {
  if (!opIds.length) return
  await runTx('readwrite', (store) => {
    opIds.forEach((opId) => store.delete(opId))
  })
}

export async function pendingOps(eventId) {
  const box = await runTx('readonly', (store) =>
    requestValue(store.index('eventQueuedAt').getAll(eventRange(eventId)))
  )
  return box.value || []
}

// Counted in the index rather than by loading the rows, so this stays cheap
// even with thousands queued - it runs after every single scan.
export async function pendingCount(eventId) {
  const box = await runTx('readonly', (store) =>
    requestValue(store.index('eventId').count(eventId))
  )
  return box.value || 0
}
